// Extend flight (campaign lifecycle 2b, 2026-10-21): what the campaign page reads off a
// campaign's extension journal, and the plan terms the builder refreshes from once one is
// in the plan.
//
// The server decides everything that matters (the floors, the horizon, whether a create may
// be retried, when the hold ends); these only word and lay out what it recorded. Two rules
// from Gio's decisions shape the words: an extension whose provider create is uncertain stays
// held with no release button, so the page says to duplicate the campaign instead; and a new
// flight starts tomorrow in New York at the earliest, after the campaign's last flight.

import type { CampaignFlightExtension, CampaignPlan } from '@/api/types';
import type { CampaignFlight } from '@/lib/campaign-flights';
import { dayYmd } from '@/lib/datetime';
import { adaptCampaignForLegacyManage } from '@/lib/manage-screen-adapters';

/** A held extension older than this reads "needs attention" (the server's alert threshold). */
export const EXTENSION_HELD_ATTENTION_MS = 60 * 60_000;

/** The one sentence for a create SAM cannot confirm (Gio, 2026-10-21). */
export const EXTENSION_UNCONFIRMED_SENTENCE =
  'Extension could not be confirmed: duplicate the campaign to run it';

/** The rows the server counts as one open extension (0030's partial unique index). */
export function extensionIsOpen(row: CampaignFlightExtension): boolean {
  return (
    row.status === 'queued' ||
    row.status === 'running' ||
    row.status === 'committed' ||
    (row.status === 'failed' &&
      (row.create_state === 'dispatched' || row.create_state === 'unknown' || row.activation_hold))
  );
}

/** Work SAM is doing now, which the page watches closely; a failed row waits on a person. */
export function extensionIsMoving(row: CampaignFlightExtension): boolean {
  return row.status === 'queued' || row.status === 'running' || row.status === 'committed';
}

/** The provider create may have landed and SAM cannot say: discovery only, held. */
export function extensionCreateUnconfirmed(row: CampaignFlightExtension): boolean {
  if (row.status === 'committed' || row.status === 'done' || row.status === 'cancelled')
    return false;
  // `dispatched` alone is the instant before the create is sent; once a pass ended on it (a
  // crash before the answer: the row failed, or back on the ladder with its reason), discovery
  // is all that is left, as for `unknown`.
  return (
    row.create_state === 'unknown'
    || (row.create_state === 'dispatched' && (row.status === 'failed' || row.last_error != null))
  );
}

export type FlightExtensionSignalKind = 'extending' | 'attention' | 'unconfirmed';

export interface FlightExtensionSignal {
  kind: FlightExtensionSignalKind;
  row: CampaignFlightExtension;
}

/**
 * What the newest extension says about the campaign, or null when it says nothing (none,
 * done, cancelled, or refused before anything was held). Newest only: an older failed row
 * a later one superseded is history.
 */
export function flightExtensionSignal(
  rows: readonly CampaignFlightExtension[] | null | undefined,
  nowMs: number,
): FlightExtensionSignal | null {
  const row = [...(rows ?? [])].sort(
    (a, b) => Date.parse(b.requested_at) - Date.parse(a.requested_at),
  )[0];
  if (!row) return null;
  if (extensionCreateUnconfirmed(row)) return { kind: 'unconfirmed', row };
  if (row.status === 'failed') return { kind: 'attention', row };
  if (!extensionIsMoving(row)) return null;
  // The row carries no hold timestamp; the request is at most a few seconds before it.
  const age = nowMs - Date.parse(row.requested_at);
  if (row.activation_hold && Number.isFinite(age) && age > EXTENSION_HELD_ATTENTION_MS) {
    return { kind: 'attention', row };
  }
  return { kind: 'extending', row };
}

/** `YYYY-MM-DD` plus `days`, on the calendar (no time zone involved). */
export function addCalendarDays(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  return dayYmd(Date.UTC(y, m - 1, d + days));
}

/**
 * The earliest day a new flight may start: tomorrow in New York, and the day after the
 * campaign's last flight, whichever is later. The route enforces the same rule.
 */
export function earliestExtensionStart(
  todayNy: string,
  lastFlightEnd: string | null | undefined,
): string {
  const tomorrow = addCalendarDays(todayNy, 1);
  const afterLast = lastFlightEnd ? addCalendarDays(lastFlightEnd, 1) : tomorrow;
  return afterLast > tomorrow ? afterLast : tomorrow;
}

/** What the dialog can refuse before asking: the server's floors come back in its words. */
export function extensionInputIssue(input: {
  start: string;
  end: string;
  budgetCents: number;
  earliestStart: string;
  latestEnd: string;
}): string | null {
  if (!input.start || !input.end) return 'Choose a start and an end date.';
  if (input.start < input.earliestStart)
    return 'The new flight starts after the last flight, and tomorrow at the earliest.';
  if (input.end < input.start) return 'The end date is before the start date.';
  if (input.end > input.latestEnd)
    return 'The end date is past the furthest a campaign can be scheduled.';
  if (!(input.budgetCents > 0)) return 'Enter the gross budget for the new flight.';
  return null;
}

/** The builder's schedule terms, as the edit route seeds them from a plan. */
export interface PlanTerms {
  budget: number;
  startDate: string;
  endDate: string;
  flights: CampaignFlight[];
}

export function planTerms(plan: CampaignPlan): PlanTerms {
  const legacy = adaptCampaignForLegacyManage(plan.campaign);
  return {
    budget: legacy.budget,
    startDate: legacy.startDate,
    endDate: legacy.endDate,
    flights: legacy.flights,
  };
}

/** The plan carries a flight the builder does not: an extension landed in it. */
export function planAddsFlights(plan: CampaignPlan, flights: readonly CampaignFlight[]): boolean {
  const known = new Set(flights.map((flight) => flight.id));
  return plan.campaign.flights.some((flight) => !known.has(flight.id));
}
